import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { Product } from '@core/models/product.model';
import { IAs2Message } from '@core/models/as2-message.model';
import { getAs2MessageThunk } from '@redux/features/as2-integration.slice';
import { getProductsThunk } from '@redux/features/product.slice';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getWarehousesThunk } from '@redux/features/warehouse.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import InboundValidationLayersPanel from 'components/integrations/InboundValidationLayersPanel';
import InboundAdminStagingForm, {
    applyPreviewHints,
    emptyInboundStaging,
    InboundStagingFormState,
    stagingFromApi,
    toStagingSavePayload,
} from 'components/integrations/InboundAdminStagingForm';
import { parseEpcisPreview } from 'components/integrations/epcis-preview.helpers';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import As2SectionNav from './As2SectionNav';
import { inferFormatSupported, isInboundValidationPassing, parseL2StructuralFallback, parsePreviewSchemaWarnings, parseSemanticWarnings, parsePreviewDscsaCompliant, parsePreviewDscsaViolations } from '../SftpIntegration/sftp-transfer-detail.utils';
import {
    as2DirectionBadgeClass,
    as2MdnStatusBadgeClass,
    as2StatusBadgeClass,
    canApproveOrReject,
    canRetry,
    formatAs2Direction,
    formatAs2MdnStatus,
    formatAs2Status,
    formatAs2Timestamp,
    formatFileSize,
} from './as2-status.utils';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-2">
            <span className="min-w-[170px] shrink-0 text-sm font-medium text-slate-500">{label}</span>
            <span className="text-sm text-slate-800">{value ?? '—'}</span>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function CollapsiblePanel({
    title,
    defaultOpen = false,
    loading,
    emptyText = 'No content available',
    content,
    onCopy,
    onExpand,
}: {
    title: string;
    defaultOpen?: boolean;
    loading?: boolean;
    emptyText?: string;
    content?: string | null;
    onCopy?: () => void;
    onExpand?: () => void;
}) {
    const [open, setOpen] = useState(defaultOpen);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && onExpand) onExpand();
    };

    return (
        <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between px-4 py-2">
                <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left text-sm font-medium text-slate-800 hover:text-[#bd902e]"
                    onClick={toggle}
                >
                    <span>{open ? '▼' : '▶'}</span>
                    {title}
                </button>
                {open && content && onCopy && (
                    <button
                        type="button"
                        onClick={onCopy}
                        className="ml-2 rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                    >
                        Copy to clipboard
                    </button>
                )}
            </div>
            {open && (
                <div className="border-t border-slate-100 px-4 py-3">
                    {loading && <p className="text-xs text-slate-400">Loading…</p>}
                    {!loading && !content && <p className="text-xs text-slate-400">{emptyText}</p>}
                    {!loading && content && (
                        <pre className="max-h-80 overflow-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 whitespace-pre-wrap font-mono">
                            {content}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
}

const As2MessageDetail: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { selected, detailLoading } = useAppSelector((state: RootState) => state.as2IntegrationState);
    const { warehouses } = useAppSelector((state: RootState) => state.warehouseState);
    const { suppliers } = useAppSelector((state: RootState) => state.supplierState);
    const { products } = useAppSelector((state: RootState) => state.productState);

    const [comment, setComment] = useState('');
    const [staging, setStaging] = useState<InboundStagingFormState>(emptyInboundStaging);
    const [stagingLoaded, setStagingLoaded] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [xmlContent, setXmlContent] = useState<string | null>(null);
    const [mdnContent, setMdnContent] = useState<string | null>(null);
    const [xmlLoading, setXmlLoading] = useState(false);
    const [mdnLoading, setMdnLoading] = useState(false);
    const [xmlLoaded, setXmlLoaded] = useState(false);
    const [mdnLoaded, setMdnLoaded] = useState(false);
    const [related, setRelated] = useState<IAs2Message[]>([]);

    const reload = useCallback(() => {
        if (id) dispatch(getAs2MessageThunk(Number(id)));
    }, [dispatch, id]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        dispatch(getWarehousesThunk());
        dispatch(getSuppliersThunk());
        dispatch(getProductsThunk({ page: 0, productSize: 1000 }));
    }, [dispatch]);

    const productList: Product[] = useMemo(() => (products?.content || []) as Product[], [products]);

    const loadStaging = useCallback(async () => {
        if (!id) return;
        try {
            const r: any = await as2IntegrationService.getStaging(Number(id));
            const dto = r.data?.staging;
            let next = stagingFromApi(dto, dto?.previewJson || selected?.validationPreviewJson, productList);
            next = applyPreviewHints(next, dto?.previewJson || selected?.validationPreviewJson, productList);
            setStaging(next);
            setStagingLoaded(true);
        } catch {
            setStaging(emptyInboundStaging());
            setStagingLoaded(true);
        }
    }, [id, selected?.validationPreviewJson, productList]);

    useEffect(() => {
        if (id && selected?.direction === 'IN') {
            loadStaging();
        }
    }, [id, selected?.direction, loadStaging]);

    const saveStaging = async () => {
        if (!id) return false;
        setActionLoading(true);
        try {
            await as2IntegrationService.saveStaging(Number(id), toStagingSavePayload(staging));
            toast.success('Staging saved');
            await loadStaging();
            return true;
        } catch {
            return false;
        } finally {
            setActionLoading(false);
        }
    };

    const previewSummary = useMemo(
        () => parseEpcisPreview(selected?.validationPreviewJson),
        [selected?.validationPreviewJson]
    );

    const stagingComplete = useMemo(() => {
        if (staging.prefferedSupplierId == null || staging.warehouseId == null) return false;
        const multiLine = staging.productLines.length > 1 || previewSummary?.multiProduct;
        if (multiLine) {
            if (!staging.purchaseOrderNumber.trim()) return false;
            return staging.productLines.every(
                (line) =>
                    line.productId != null &&
                    line.lotNumber.trim() !== '' &&
                    line.expiryDate !== '' &&
                    line.palletSscc.trim() !== ''
            );
        }
        if (staging.productLines.length === 1) {
            const line = staging.productLines[0];
            return (
                line.productId != null &&
                line.lotNumber.trim() !== '' &&
                line.expiryDate !== ''
            );
        }
        return (
            staging.productId != null &&
            staging.lotNumber.trim() !== '' &&
            staging.expiryDate !== ''
        );
    }, [staging, previewSummary]);

    useEffect(() => {
        if (!id) return;
        as2IntegrationService.getRelated(Number(id)).then((r: any) => {
            setRelated(r.data.relatedMessages || []);
        }).catch(() => setRelated([]));
    }, [id]);

    const loadXml = useCallback(async () => {
        if (!id || xmlLoaded) return;
        setXmlLoading(true);
        try {
            const r: any = await as2IntegrationService.getXmlContent(Number(id));
            setXmlContent(r.data.xmlContent || null);
            setXmlLoaded(true);
        } catch {
            setXmlContent(null);
        } finally {
            setXmlLoading(false);
        }
    }, [id, xmlLoaded]);

    const loadMdn = useCallback(async () => {
        if (!id || mdnLoaded) return;
        setMdnLoading(true);
        try {
            const r: any = await as2IntegrationService.getMdnContent(Number(id));
            setMdnContent(r.data.mdnContent || null);
            setMdnLoaded(true);
        } catch {
            setMdnContent(null);
        } finally {
            setMdnLoading(false);
        }
    }, [id, mdnLoaded]);

    const copyText = async (text: string | null | undefined, label: string) => {
        if (!text) {
            toast.info(`No ${label} to copy`);
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch {
            toast.error('Copy failed');
        }
    };

    const reprocessValidation = async () => {
        if (!id) return;
        setActionLoading(true);
        try {
            await as2IntegrationService.reprocessValidation(Number(id));
            toast.success('Validation re-run complete');
            reload();
        } catch {
            /* interceptor */
        } finally {
            setActionLoading(false);
        }
    };

    const approve = async () => {
        if (!id) return;
        if (!stagingComplete) {
            toast.error('Complete and save admin staging before approve');
            return;
        }
        if (
            selected?.direction === 'IN' &&
            selected?.validationPreviewJson?.trim() &&
            previewSummary &&
            !previewSummary.despatchAdviceHint
        ) {
            toast.error('Shipment ID not found in preview — re-run validation before approve.');
            return;
        }
        const multiLine = staging.productLines.length > 1 || previewSummary?.multiProduct;
        if (multiLine && !staging.purchaseOrderNumber.trim()) {
            toast.error('Purchase order number is required for multi-product inbound');
            return;
        }
        if (staging.productLines.length > 0) {
            for (const line of staging.productLines) {
                if (!line.productId) {
                    toast.error('Select a catalog product for each EPCIS line');
                    return;
                }
                const qty = Number(line.quantity) || 0;
                const serialCount = line.serialNumbers.filter((s) => s.trim()).length;
                if (serialCount > 0 && qty !== serialCount) {
                    toast.error(`Quantity must match serial count for ${line.epcisProductLineKey}`);
                    return;
                }
            }
        } else {
            const qty = Number(staging.quantity) || 0;
            const serialCount = staging.serialNumbers.filter((s) => s.trim()).length;
            if (serialCount > 0 && qty !== serialCount) {
                toast.error('Quantity must match the number of serial numbers');
                return;
            }
        }
        setActionLoading(true);
        try {
            await as2IntegrationService.saveStaging(Number(id), toStagingSavePayload(staging));
            await as2IntegrationService.approve(Number(id), comment || undefined);
            const identityKeys =
                previewSummary?.productLines?.filter((l) => l.sgtinItemKey).length ??
                (previewSummary?.sgtinItemKey ? 1 : 0);
            toast.success(
                identityKeys > 0
                    ? `Approved — product identity (GTIN/prefix/itemRef) applied from EPCIS for ${identityKeys} line(s)`
                    : 'Inbound EPCIS approved and import started'
            );
            reload();
        } catch {
            /* interceptor */
        } finally {
            setActionLoading(false);
        }
    };

    const reject = async () => {
        if (!id) return;
        setActionLoading(true);
        try {
            await as2IntegrationService.reject(Number(id), comment || undefined);
            toast.info('Inbound EPCIS rejected');
            navigate(PRIVATEPATHS.As2Messages);
        } catch {
            /* interceptor */
        } finally {
            setActionLoading(false);
        }
    };

    const retry = async () => {
        if (!id) return;
        setActionLoading(true);
        try {
            await as2IntegrationService.retryAll();
            toast.success('Retry triggered. Refresh in 30 seconds.');
            reload();
        } catch {
            /* interceptor */
        } finally {
            setActionLoading(false);
        }
    };

    const msg = selected;
    const isActionable = canApproveOrReject(msg);
    const semanticWarnings = parseSemanticWarnings(msg?.validationPreviewJson);
    const schemaWarnings = parsePreviewSchemaWarnings(msg?.validationPreviewJson);
    const l2StructuralFallback = parseL2StructuralFallback(msg?.validationPreviewJson);
    const dscsaViolationList = parsePreviewDscsaViolations(msg?.validationPreviewJson);
    const dscsaCompliant = parsePreviewDscsaCompliant(msg?.validationPreviewJson);
    const formatSupported = inferFormatSupported(null, null, msg?.validationPreviewJson);
    const canApprove =
        isActionable && isInboundValidationPassing(msg?.schemaValid, dscsaCompliant);
    const isRetryable = canRetry(msg);
    const showMdnPanel = msg?.direction === 'OUT';

    return (
        <>
            <BreadCrumb pageTitle={`AS2 Message #${id}`} />
            <section className="py-5">
                <div className="flex flex-col gap-4 bg-white p-3">
                    <As2SectionNav />

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            {msg && (
                                <>
                                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${as2StatusBadgeClass(msg.status)}`}>
                                        {formatAs2Status(msg.status)}
                                    </span>
                                    <span className="text-sm font-semibold text-slate-800">PARTNER: {msg.partner}</span>
                                    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${as2DirectionBadgeClass(msg.direction)}`}>
                                        {formatAs2Direction(msg.direction)}
                                    </span>
                                </>
                            )}
                        </div>
                        <Link
                            to={PRIVATEPATHS.As2Messages}
                            className="text-sm font-semibold text-slate-600 hover:text-[#bd902e] hover:underline"
                        >
                            ← Back to list
                        </Link>
                    </div>

                    {detailLoading && <p className="text-sm text-slate-500">Loading…</p>}

                    {!detailLoading && msg && (
                        <>
                            {isActionable && (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                    <strong>Inbound EPCIS awaiting your review.</strong> Expand the XML viewer below, then Approve or Reject.
                                </div>
                            )}
                            {isRetryable && (
                                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                                    <strong>Delivery failed.</strong>{' '}
                                    <code className="font-mono">{msg.lastError || 'See OpenAS2 logs'}</code>
                                </div>
                            )}
                            {msg.status === 'MDN_RECEIVED' && (
                                <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                                    <strong>MDN received.</strong> Partner confirmed receipt. DSCSA obligation met.
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                <Section title="Message information">
                                    <InfoRow label="Message ID" value={<span className="font-mono text-xs">{msg.messageId}</span>} />
                                    <InfoRow label="AS2-From" value={msg.as2From} />
                                    <InfoRow label="AS2-To" value={msg.as2To} />
                                    <InfoRow label="Subject" value={msg.subject} />
                                    <InfoRow label="Outbox file" value={msg.outboxFileName} />
                                    <InfoRow label="File size" value={formatFileSize(msg.fileSizeBytes)} />
                                    <InfoRow
                                        label="Inbound doc"
                                        value={
                                            msg.epcisInboundDocumentId ? (
                                                <span className="font-medium text-green-700">
                                                    #{msg.epcisInboundDocumentId} imported
                                                </span>
                                            ) : '—'
                                        }
                                    />
                                    <InfoRow
                                        label="Outbound EPCIS"
                                        value={
                                            msg.epcisId ? (
                                                <Link to={PRIVATEPATHS.Epcis} className="font-medium text-[#bd902e] hover:underline">
                                                    #{msg.epcisId} →
                                                </Link>
                                            ) : '—'
                                        }
                                    />
                                    <InfoRow label="Correlation ID" value={<span className="font-mono text-xs">{msg.correlationId}</span>} />
                                    <InfoRow label="Retry count" value={msg.retryCount ?? 0} />
                                    {msg.batchId ? (
                                        <InfoRow
                                            label="Batch"
                                            value={
                                                <Link
                                                    to={`${PRIVATEPATHS.Batch}/${msg.batchId}`}
                                                    className="font-medium text-[#bd902e] hover:underline"
                                                >
                                                    Batch #{msg.batchId}
                                                </Link>
                                            }
                                        />
                                    ) : null}
                                    {msg.adminComment && (
                                        <InfoRow label="Admin comment" value={<span className="italic">{msg.adminComment}</span>} />
                                    )}
                                </Section>

                                <Section title="Delivery timeline">
                                    <ol className="relative border-l border-slate-200 pl-4">
                                        <TimelineItem label="Created" time={msg.createdAt} done={!!msg.createdAt} />
                                        <TimelineItem
                                            label="Dispatched to OpenAS2"
                                            time={msg.updatedAt}
                                            done={['DISPATCHED', 'SENT', 'MDN_RECEIVED', 'FAILED'].includes(msg.status ?? '')}
                                            status={msg.status}
                                        />
                                        <TimelineItem label="Sent by OpenAS2" time={msg.sentAt} done={['SENT', 'MDN_RECEIVED'].includes(msg.status ?? '')} />
                                        <TimelineItem label="MDN received" time={msg.mdnReceivedAt} done={msg.status === 'MDN_RECEIVED'} isFinal />
                                    </ol>
                                </Section>
                            </div>

                            {msg.direction === 'IN' && (
                                <Section title="Inbound EPCIS validation">
                                    <div className="mb-3">
                                        <InboundValidationLayersPanel
                                            formatSupported={formatSupported}
                                            schemaValid={msg.schemaValid}
                                            dscsaCompliant={dscsaCompliant}
                                            semanticWarningCount={semanticWarnings.length}
                                            schemaWarningCount={schemaWarnings.length}
                                            l2StructuralFallbackUsed={l2StructuralFallback}
                                        />
                                    </div>
                                    <InfoRow
                                        label="Schema valid"
                                        value={msg.schemaValid == null ? '—' : msg.schemaValid ? 'Yes' : 'No'}
                                    />
                                    <InfoRow
                                        label="DSCSA R1.3"
                                        value={
                                            dscsaCompliant === true ? (
                                                <span className="font-medium text-green-700">Compliant</span>
                                            ) : dscsaCompliant === false ? (
                                                <span className="font-medium text-red-600">Not compliant</span>
                                            ) : (
                                                '—'
                                            )
                                        }
                                    />
                                    {dscsaViolationList.length > 0 && (
                                        <div className="rounded border border-red-200 bg-red-50 p-3">
                                            <p className="mb-1 text-xs font-semibold text-red-900">DSCSA violations</p>
                                            <ul className="list-inside list-disc text-xs text-red-900">
                                                {dscsaViolationList.map((v) => (
                                                    <li key={v}>{v}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {semanticWarnings.length > 0 && (
                                        <div className="rounded border border-amber-200 bg-amber-50 p-3">
                                            <p className="mb-1 text-xs font-semibold text-amber-900">Semantic warnings</p>
                                            <ul className="list-inside list-disc text-xs text-amber-900">
                                                {semanticWarnings.map((w) => (
                                                    <li key={w}>{w}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {msg.validationPreviewJson && (
                                        <CollapsiblePanel
                                            title="Validation preview"
                                            defaultOpen
                                            content={msg.validationPreviewJson}
                                            onCopy={() => {
                                                navigator.clipboard.writeText(msg.validationPreviewJson || '');
                                                toast.success('Copied validation preview');
                                            }}
                                        />
                                    )}
                                </Section>
                            )}

                            {/* MDN receipt panel */}
                            {showMdnPanel && (
                                <Section title="MDN receipt (Message Disposition Notification)">
                                    <InfoRow
                                        label="MDN status"
                                        value={
                                            <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${as2MdnStatusBadgeClass(msg.mdnStatus)}`}>
                                                {formatAs2MdnStatus(
                                                    msg.mdnStatus,
                                                    msg.status === 'FAILED' ? msg.lastError : undefined
                                                )}
                                            </span>
                                        }
                                    />
                                    {msg.status === 'FAILED' && msg.lastError && (
                                        <InfoRow
                                            label="MDN disposition"
                                            value={<span className="font-mono text-xs text-red-800">{msg.lastError}</span>}
                                        />
                                    )}
                                    <InfoRow label="MDN Message-ID" value={<span className="font-mono text-xs">{msg.mdnMessageId}</span>} />
                                    <InfoRow label="Received at" value={formatAs2Timestamp(msg.mdnReceivedAt)} />
                                    <CollapsiblePanel
                                        title="View raw MDN file"
                                        loading={mdnLoading}
                                        content={mdnContent}
                                        emptyText="MDN file not yet available (message may still be in transit)"
                                        onExpand={loadMdn}
                                        onCopy={() => copyText(mdnContent, 'MDN')}
                                    />
                                </Section>
                            )}

                            {/* EPCIS XML — load on demand */}
                            <CollapsiblePanel
                                title="EPCIS XML content"
                                defaultOpen={isActionable}
                                loading={xmlLoading}
                                content={xmlContent}
                                emptyText="XML file not found on disk"
                                onExpand={loadXml}
                                onCopy={() => copyText(xmlContent, 'XML')}
                            />

                            {/* Retry chain table */}
                            {related.length > 1 && (
                                <Section title={`Retry chain — all attempts for EPCIS #${msg.epcisId} → ${msg.partner}`}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-xs">
                                            <thead>
                                                <tr className="border-b border-slate-200 text-slate-500">
                                                    <th className="py-2 pr-3">ID</th>
                                                    <th className="py-2 pr-3">Status</th>
                                                    <th className="py-2 pr-3">Created</th>
                                                    <th className="py-2 pr-3">MDN received</th>
                                                    <th className="py-2">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {related.map((r) => (
                                                    <tr key={r.id} className={`border-b border-slate-100 ${r.id === msg.id ? 'bg-[#bd902e10]' : ''}`}>
                                                        <td className="py-2 pr-3 font-medium">{r.id}</td>
                                                        <td className="py-2 pr-3">
                                                            <span className={`inline-flex rounded px-1.5 py-0.5 font-medium ${as2StatusBadgeClass(r.status)}`}>
                                                                {formatAs2Status(r.status)}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 pr-3 text-slate-500">{formatAs2Timestamp(r.createdAt)}</td>
                                                        <td className="py-2 pr-3 text-slate-500">{formatAs2Timestamp(r.mdnReceivedAt)}</td>
                                                        <td className="py-2">
                                                            {r.id === msg.id ? (
                                                                <span className="text-slate-400">current</span>
                                                            ) : (
                                                                <Link to={`${PRIVATEPATHS.As2Messages}/${r.id}`} className="text-[#bd902e] hover:underline">
                                                                    View
                                                                </Link>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Section>
                            )}

                            {msg.direction === 'IN' && isActionable && stagingLoaded && (
                                <div>
                                    <h4 className="mb-3 font-semibold text-slate-800">Admin staging</h4>
                                    {!stagingComplete && (
                                        <p className="mb-3 text-sm text-amber-800">
                                            Required before approve: supplier, product, warehouse, lot number, and expiry date.
                                        </p>
                                    )}
                                    <InboundAdminStagingForm
                                        staging={staging}
                                        setStaging={setStaging}
                                        disabled={actionLoading}
                                        suppliers={suppliers || []}
                                        products={productList}
                                        warehouses={warehouses || []}
                                        validationPreviewJson={
                                            selected?.validationPreviewJson ?? undefined
                                        }
                                    />
                                    <div className="mt-4">
                                        <ButtonBase
                                            innerText={actionLoading ? 'Saving…' : 'Save staging'}
                                            onClick={saveStaging}
                                            disabled={actionLoading}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Admin actions */}
                            <div className="flex flex-wrap gap-2">
                                {isActionable && msg?.schemaValid === false && (
                                    <p className="w-full text-sm text-red-700">
                                        Approve is blocked until validation passes (schema and DSCSA R1.3).
                                    </p>
                                )}
                                {isActionable && msg?.schemaValid !== false && dscsaCompliant === false && (
                                    <p className="w-full text-sm text-red-700">
                                        Approve is blocked — DSCSA R1.3 validation failed. Fix the file or reject.
                                    </p>
                                )}
                                {isActionable && (
                                    <ButtonBase
                                        innerText={actionLoading ? 'Validating…' : 'Re-run validation'}
                                        onClick={reprocessValidation}
                                        disabled={actionLoading}
                                    />
                                )}
                                {isActionable && (
                                    <>
                                        <div className="w-full">
                                            <label className="mb-1 block text-sm font-medium text-slate-700">Comment (optional)</label>
                                            <textarea
                                                className="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#bd902e]"
                                                rows={2}
                                                placeholder="Add a note for the audit log…"
                                                value={comment}
                                                onChange={(e) => setComment(e.target.value)}
                                            />
                                        </div>
                                        <ButtonBase
                                            innerText={actionLoading ? 'Approving…' : 'Approve'}
                                            onClick={approve}
                                            disabled={actionLoading || !canApprove || !stagingComplete}
                                        />
                                        <button
                                            type="button"
                                            onClick={reject}
                                            disabled={actionLoading}
                                            className="inline-flex items-center rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                        >
                                            {actionLoading ? 'Rejecting…' : 'Reject'}
                                        </button>
                                    </>
                                )}
                                {isRetryable && (
                                    <ButtonBase innerText={actionLoading ? 'Retrying…' : 'Retry delivery'} onClick={retry} disabled={actionLoading} />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

function TimelineItem({
    label,
    time,
    done,
    isFinal,
    status,
}: {
    label: string;
    time?: string;
    done?: boolean;
    isFinal?: boolean;
    status?: string;
}) {
    const failed = status === 'FAILED';
    return (
        <li className="mb-6 last:mb-0">
            <span
                className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                    failed ? 'border-red-500 bg-red-100' : done ? 'border-green-500 bg-green-100' : 'border-slate-300 bg-white'
                }`}
            >
                {failed && <span className="block h-1.5 w-1.5 rounded-full bg-red-500" />}
                {done && !failed && <span className="block h-1.5 w-1.5 rounded-full bg-green-500" />}
            </span>
            <div className="ml-2">
                <p className={`text-xs font-medium ${done && !failed ? 'text-green-700' : failed ? 'text-red-700' : 'text-slate-500'}`}>
                    {label}{isFinal && done ? ' ✓' : ''}
                </p>
                {time && <p className="text-xs text-slate-400">{formatAs2Timestamp(time)}</p>}
            </div>
        </li>
    );
}

export default As2MessageDetail;
